import React from 'react';
import PropTypes from 'prop-types';
import { compose, lifecycle, pure, withState } from 'recompose';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Form, Field } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import createDecorator from 'final-form-focus';
import * as R from 'ramda';

import * as actions from 'actions';
import { getUserContactId } from 'selectors';

import { clientId, clientSecret } from 'config';
import { Button, Link } from 'components/shared';
import { FormBox, FormGroupAdapter, FormStepHeader, NarrowFormFlex, FormAsterisk } from 'components/shared/form';
import Well, { WellBody, WellHeader, WellFooter } from 'components/shared/Well';

import { PRIVACY_POLICY } from 'constants/routes';
import { holdMutators, validateAccepted, setIn, getReferralTokenCookie } from 'utils';

import { WrittenConsentProvidedCheckbox } from '../../shared';
import Fields from './Fields';

const propTypes = {
  sendFirstStep: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({}),
};

const defaultProps = {
  initialValues: {},
};

const focusOnErrors = createDecorator();

function Step1({ initialValues, sendFirstStep }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type');
  const isNewConstruction = type === 'new_construction';
  const title = isNewConstruction ? 'Apply For New Construction Loan' : 'Apply For Fix/Flip or Bridge Loan';
  const referralToken = getReferralTokenCookie();

  return (
    <Form
      onSubmit={sendFirstStep}
      initialValues={initialValues}
      decorators={[focusOnErrors]}
      subscription={{ submitting: true, pristine: true }}
      mutators={{ ...holdMutators }}
      render={({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Field
            name="application.new_construction"
            component="input"
            type="hidden"
            initialValue={isNewConstruction}
          />

          <Field
            name="application.referral_token"
            component="input"
            type="hidden"
            initialValue={referralToken}
          />

          <Well>
            <WellHeader key="header">
              <FormStepHeader
                title={title}
                subtitle="Step 1 out of 2"
                current={1}
                total={2}
              />
            </WellHeader>

            <WellBody key="body">
              <Fields />
            </WellBody>

            <WellFooter key="footer">
              <NarrowFormFlex flexDirection="column">
                <FormBox>
                  <Field
                    name="application.serviceTerms"
                    mb={0}
                    type="checkbox"
                    fz={0.825}
                    validate={validateAccepted()}
                    component={FormGroupAdapter}
                  >
                    <FormAsterisk>
                      I agree to LendingOne’s{' '}
                      <Link to={PRIVACY_POLICY} target="_blank">
                        Privacy Policy and Terms of Use
                      </Link>{' '}
                      , including mandatory arbitration.
                    </FormAsterisk>
                  </Field>

                  <WrittenConsentProvidedCheckbox />
                </FormBox>

                <FormBox alignSelf="flex-end">
                  <Button type="submit" dataName="buttonNext" rounded disabled={submitting}>
                    {submitting ? 'Please wait…' : 'Next'}
                  </Button>
                </FormBox>
              </NarrowFormFlex>
            </WellFooter>
          </Well>
        </form>
      )}
    />
  );
}

function mapStateToProps(state) {
  return {
    userContactId: getUserContactId(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    sendFirstStep: bindActionCreators(actions.createBridgeApplication, dispatch),
    dataLayerPush: bindActionCreators(actions.dataLayerPush, dispatch),
  };
}

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withState('initialValues', 'setInitialValues', ({ userContactId }) => R.when(
    () => !userContactId,
    R.pipe(
      setIn(clientId, 'clientId'),
      setIn(clientSecret, 'clientSecret'),
    ),
  )({})),
  lifecycle({
    componentDidMount() {
      const { userContactId, dataLayerPush } = this.props;
      if (userContactId) dataLayerPush({ loanType: 'bridge', userId: userContactId, bSc: '0' });
    },
  }),
  pure,
);

Step1.propTypes = propTypes;
Step1.defaultProps = defaultProps;

export default enhance(Step1);
